// src/pages/appointments/AppointmentsNearby.jsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import DoctorMap from "@/components/maps/DoctorMap.jsx";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";
import useGeolocation from "@/hooks/useGeolocation.js";
import LocationConsentDialog from "@/components/location/LocationConsentDialog.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import supabase from "@/services/supabaseClient";

// Neutral fallback center (AfriMed regional HQ: Cape Town)
const DEMO_CENTER = { lat: -33.9249, lng: 18.4241 };

const ICONS = {
  search: "/icons/doctor/search.svg",
  cancel: "/icons/doctor/cancel.svg",
  nearMe: "/icons/doctor/near_me_filled.svg",
};

function toNumber(value) {
  const n = Number(String(value ?? "").trim());
  return Number.isFinite(n) ? n : null;
}

function isValidLatLng(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return false;
  return true;
}

function normalizeFacility(item) {
  if (!item) return null;
  const lat = toNumber(item.lat ?? item.latitude);
  const lng = toNumber(item.lng ?? item.longitude ?? item.lon);
  if (!isValidLatLng(lat, lng)) return null;

  return {
    id: item.id ? String(item.id) : null,
    name: String(item.name || item.facility_name || "").trim(),
    address: String(item.address || item.street_address || item.facility_address || "").trim(),
    city: String(item.city || item.facility_city || "").trim(),
    country: String(item.country || item.facility_country || "").trim(),
    lat,
    lng,
  };
}

export default function AppointmentsNearby() {
  const nav = useNavigate();
  const location = useLocation();
  const geo = useGeolocation();
  const { t } = useLanguage();
  const { isDark } = useTheme();

  const [query, setQuery] = React.useState("");
  const [allFacilities, setAllFacilities] = React.useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = React.useState(false);
  const [selectedFacility, setSelectedFacility] = React.useState(null);
  const [mapRef, setMapRef] = React.useState(null);

  const queryParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const queryFacilityId = String(queryParams.get("facilityId") || "").trim() || null;
  const queryFacilityName = String(queryParams.get("facilityName") || "").trim();
  const queryLat = toNumber(queryParams.get("facilityLat") ?? queryParams.get("lat"));
  const queryLng = toNumber(queryParams.get("facilityLng") ?? queryParams.get("lng"));
  const hasQueryCoords = isValidLatLng(queryLat, queryLng);

  const queryFacility = React.useMemo(() => {
    if (!queryFacilityId && !queryFacilityName && !hasQueryCoords) return null;
    return normalizeFacility({
      id: queryFacilityId,
      name:
        queryFacilityName ||
        t("home.patient.nearby.defaultFacilityName", "Healthcare facility"),
      lat: queryLat,
      lng: queryLng,
    });
  }, [queryFacilityId, queryFacilityName, queryLat, queryLng, hasQueryCoords, t]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadFacilitiesFromCsv() {
      const res = await fetch("/data/health_facilities_sadc.csv");
      const text = await res.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length <= 1) return [];

      lines.shift(); // header
      return lines
        .map((line, idx) => {
          const cols = line.split(",");
          return normalizeFacility({
            id: `csv-${idx}`,
            country: String(cols[0] || "").trim(),
            city: String(cols[1] || "").trim(),
            name: String(cols[2] || "").trim(),
            lat: cols[5],
            lng: cols[6],
          });
        })
        .filter(Boolean);
    }

    async function loadFacilities() {
      setFacilitiesLoading(true);
      try {
        const [{ data: dbRows, error: dbErr }, csvRows] = await Promise.all([
          supabase
            .from("healthcare_locations")
            .select(
              "id, facility_name, street_address, city, country, latitude, longitude"
            )
            .limit(2000),
          loadFacilitiesFromCsv(),
        ]);

        const parsedDb =
          !dbErr && Array.isArray(dbRows)
            ? dbRows
                .map((row) =>
                  normalizeFacility({
                    id: row.id,
                    name: row.facility_name,
                    address: row.street_address,
                    city: row.city,
                    country: row.country,
                    lat: row.latitude,
                    lng: row.longitude,
                  })
                )
                .filter(Boolean)
            : [];

        const merged = [...parsedDb, ...(Array.isArray(csvRows) ? csvRows : [])];
        const deduped = [];
        const seen = new Set();
        for (const item of merged) {
          const key = `${String(item.name || "").toLowerCase()}|${item.lat.toFixed(
            5
          )}|${item.lng.toFixed(5)}`;
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push(item);
        }

        if (!cancelled) setAllFacilities(deduped);
      } catch {
        try {
          const csvRows = await loadFacilitiesFromCsv();
          if (!cancelled) setAllFacilities(csvRows);
        } catch {
          if (!cancelled) setAllFacilities([]);
        }
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    }

    loadFacilities();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (selectedFacility) return;
    if (!queryFacility) return;

    const fromLoaded =
      allFacilities.find((f) => {
        if (queryFacilityId && String(f.id || "") === queryFacilityId) return true;
        if (queryFacilityName && f.name.toLowerCase() === queryFacilityName.toLowerCase())
          return true;
        if (
          hasQueryCoords &&
          Number(f.lat).toFixed(5) === Number(queryLat).toFixed(5) &&
          Number(f.lng).toFixed(5) === Number(queryLng).toFixed(5)
        ) {
          return true;
        }
        return false;
      }) || null;

    setSelectedFacility(fromLoaded || queryFacility);
  }, [
    allFacilities,
    hasQueryCoords,
    queryFacility,
    queryFacilityId,
    queryFacilityName,
    queryLat,
    queryLng,
    selectedFacility,
  ]);

  React.useEffect(() => {
    if (geo?.consent === "always" && geo?.status === "granted") {
      geo.startWatch();
      return () => geo.stopWatch();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo?.consent, geo?.status]);

  const handleNearMe = React.useCallback(async () => {
    if (geo.coords?.lat != null && geo.coords?.lng != null) {
      if (mapRef) {
        mapRef.flyTo({
          center: [Number(geo.coords.lng), Number(geo.coords.lat)],
          zoom: 13,
          duration: 800,
        });
      }
      return;
    }
    if (geo.needsConsent) {
      geo.openDialog();
      return;
    }
    if (geo.status === "denied" || geo.consent === "denied") return;
    geo.getOnce();
  }, [geo, mapRef]);

  React.useEffect(() => {
    if (!mapRef) return;
    if (geo?.status !== "granted") return;
    if (geo?.coords?.lat == null || geo?.coords?.lng == null) return;

    mapRef.flyTo({
      center: [Number(geo.coords.lng), Number(geo.coords.lat)],
      zoom: 13,
      duration: 800,
    });
  }, [geo?.coords?.lat, geo?.coords?.lng, geo?.status, mapRef]);

  const filteredFacilities = React.useMemo(() => {
    const term = String(query || "").trim().toLowerCase();
    if (!term) return allFacilities;
    return allFacilities.filter((item) =>
      [item.name, item.address, item.city, item.country]
        .map((v) => String(v || "").toLowerCase())
        .some((v) => v.includes(term))
    );
  }, [allFacilities, query]);

  const mapCenter = React.useMemo(() => {
    if (
      selectedFacility &&
      isValidLatLng(Number(selectedFacility.lat), Number(selectedFacility.lng))
    ) {
      return { lat: Number(selectedFacility.lat), lng: Number(selectedFacility.lng) };
    }
    if (geo?.status === "granted" && isValidLatLng(geo?.coords?.lat, geo?.coords?.lng)) {
      return { lat: Number(geo.coords.lat), lng: Number(geo.coords.lng) };
    }
    if (queryFacility && isValidLatLng(Number(queryFacility.lat), Number(queryFacility.lng))) {
      return { lat: Number(queryFacility.lat), lng: Number(queryFacility.lng) };
    }
    return DEMO_CENTER;
  }, [geo?.coords?.lat, geo?.coords?.lng, geo?.status, queryFacility, selectedFacility]);

  const focusTarget = React.useMemo(() => {
    if (!selectedFacility) return null;
    if (!isValidLatLng(Number(selectedFacility.lat), Number(selectedFacility.lng)))
      return null;
    return {
      id: selectedFacility.id || null,
      name:
        selectedFacility.name ||
        t("home.patient.nearby.defaultFacilityName", "Healthcare facility"),
      address: selectedFacility.address || "",
      city: selectedFacility.city || "",
      country: selectedFacility.country || "",
      lat: Number(selectedFacility.lat),
      lng: Number(selectedFacility.lng),
      zoom: 14,
    };
  }, [selectedFacility, t]);

  const openDoctors = React.useCallback(() => {
    const facility = selectedFacility || queryFacility;
    if (!facility) {
      nav("/appointments/doctors");
      return;
    }

    const qs = new URLSearchParams();
    if (facility.id) qs.set("facilityId", String(facility.id));
    if (facility.name) {
      qs.set("facilityName", String(facility.name));
      qs.set("q", String(facility.name));
    }
    if (isValidLatLng(Number(facility.lat), Number(facility.lng))) {
      qs.set("facilityLat", String(Number(facility.lat)));
      qs.set("facilityLng", String(Number(facility.lng)));
    }
    const url = qs.toString()
      ? `/appointments/doctors?${qs.toString()}`
      : "/appointments/doctors";
    nav(url);
  }, [nav, queryFacility, selectedFacility]);

  const canShowUser =
    geo?.status === "granted" &&
    isValidLatLng(Number(geo?.coords?.lat), Number(geo?.coords?.lng));

  const activeFacility = selectedFacility || queryFacility;

  return (
    <div data-hide-tabbar="true">
      <div className="afrimed-rail">
        <header className="mb-3">
          <TopBarCenter
            title={t("appointments.nearby.pageTitle", "Appointments")}
            close
            onBack={() => nav(-1)}
          />
        </header>

        <div className="mb-3">
          <div className="flex items-center gap-2 h-12 rounded-full bg-transparent px-4">
            <img src={ICONS.search} alt="" className="h-6 w-6" draggable="false" />

            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("home.patient.nearby.searchPlaceholder", "Search facilities")}
              className="flex-1 bg-transparent outline-none text-body-large text-on-surface placeholder:text-on-surface-var"
            />

            {query.trim() ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="h-10 w-10 grid place-items-center rounded-full"
                aria-label={t("home.patient.nearby.clearSearch", "Clear search")}
              >
                <img src={ICONS.cancel} alt="" className="h-6 w-6" draggable="false" />
              </button>
            ) : null}
          </div>

          <div className="mt-3 h-px w-full bg-[rgba(91,107,134,0.20)]" />
        </div>

        <div className="mb-2">
          <div className="text-title-medium text-on-surface-var">
            {t("home.patient.nearby.title", "Healthcare near you")}
          </div>
        </div>

        <section className="mb-4">
          <div className="relative">
            {activeFacility?.name ? (
              <div className="absolute top-3 left-3 right-3 z-10 pointer-events-none">
                <div className="pointer-events-auto flex items-center justify-between gap-3 rounded-[999px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-2 shadow-[0_2px_8px_rgba(15,23,42,0.10)] border border-[rgba(91,107,134,0.20)]">
                  <div className="text-body-medium text-on-surface-var truncate">
                    {t("home.patient.nearby.showingNear", "Showing facilities near")}{" "}
                    <span className="text-on-surface">{activeFacility.name}</span>
                  </div>

                  <button
                    type="button"
                    className="h-8 w-8 grid place-items-center rounded-full hover:bg-[rgba(2,6,23,0.06)]"
                    aria-label={t(
                      "home.patient.nearby.clearSelectedFacility",
                      "Clear selected facility"
                    )}
                    onClick={() => {
                      setSelectedFacility(null);
                      nav("/appointments/nearby", { replace: true });
                    }}
                  >
                    <img
                      src="/icons/doctor/cancel.svg"
                      alt=""
                      className="h-5 w-5 opacity-70"
                      draggable="false"
                    />
                  </button>
                </div>
              </div>
            ) : null}

            <DoctorMap
              diagnosticSurface="appointments-nearby"
              center={mapCenter}
              showUser={canShowUser}
              user={
                canShowUser
                  ? { lat: Number(geo.coords.lat), lng: Number(geo.coords.lng) }
                  : null
              }
              facilities={filteredFacilities}
              doctors={[]}
              heightClass="h-[449px]"
              frameClassName="w-full overflow-hidden rounded-[12px] border border-[rgba(91,107,134,0.20)]"
              mapStyle={getAfriMedMapStyle(isDark)}
              facilityIcon="/markers/afrimed-hospital.svg"
              facilityIconNearest="/markers/afrimed-nearest-hospital.svg"
              doctorIcon="/markers/afrimed-nearest-doctor.svg"
              userIcon="/markers/afrimed-user.svg"
              highlightFacilityId={activeFacility?.id || null}
              focusTarget={focusTarget}
              onFacilitySelect={(f) => {
                const normalized = normalizeFacility(f);
                if (!normalized) return;
                setSelectedFacility(normalized);
              }}
              onFacilityPopupClose={() => setSelectedFacility(null)}
              onMapReady={(map) => setMapRef(map)}
              onRequireLocation={handleNearMe}
            />

            <button
              type="button"
              onClick={handleNearMe}
              className="absolute right-4 bottom-4 h-11 w-11 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] flex items-center justify-center shadow-[0_10px_30px_rgba(15,23,42,0.18)]"
              aria-label={t("home.patient.nearby.action", "Find healthcare near me")}
            >
              <img
                src={ICONS.nearMe}
                alt=""
                className="h-5 w-5"
                draggable="false"
              />
            </button>
          </div>
        </section>

        {activeFacility?.name ? (
          <Card className="mb-4 px-4 py-3">
            <div className="text-label-medium text-on-surface-var mb-1">
              {t("home.patient.nearby.selectedFacility", "Selected facility")}
            </div>
            <div className="text-body-large text-on-surface">{activeFacility.name}</div>
            {[activeFacility.address, [activeFacility.city, activeFacility.country].filter(Boolean).join(", ")]
              .filter(Boolean)
              .slice(0, 2)
              .map((line) => (
                <div key={line} className="text-body-small text-on-surface-var mt-1">
                  {line}
                </div>
              ))}
          </Card>
        ) : null}

        {geo.consent === "denied" && geo.error ? (
          <div className="mb-3 text-body-medium text-on-surface-var">{geo.error}</div>
        ) : null}

        {facilitiesLoading ? (
          <div className="mb-3 text-body-small text-on-surface-var">
            {t("home.patient.nearby.loading", "Loading nearby facilities...")}
          </div>
        ) : null}

        <div className="pt-1 pb-6 flex justify-center">
          <Button size="lg" className="w-[min(100%,220px)]" onClick={openDoctors}>
            {activeFacility?.name
              ? t("home.patient.nearby.seeDoctors", "See doctors at this facility")
              : t("home.patient.nearby.findDoctors", "Find doctors")}
          </Button>
        </div>
      </div>

      <LocationConsentDialog
        open={geo.dialogOpen}
        onClose={geo.closeDialog}
        onAllowOnce={geo.allowOnce}
        onAllowAlways={geo.allowAlways}
        onDeny={geo.deny}
      />
    </div>
  );
}
